import { joiResolver } from '@hookform/resolvers/joi';
import { Box, Grid, Link, Paper, Typography } from '@mui/material';
import Joi from 'joi';
import { observer } from 'mobx-react-lite';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import settings from '../../../global/config/settings';
import { API, RPO_SEARCH_URL } from '../../../global/config/api';
import {
  BaseHeader,
  XsAutocompleteServerWithoutLoad,
  XsButton,
  XsCheckbox,
  XsNumberField,
  XsTextField
} from '../../../global/ui';
import { useServerCall } from '../../../global/helpers/hooks';
import { getFilters } from '../../../global/helpers/filters';
import { isEmailValid } from '../../../global/helpers/validators';
import GlobalStore from '../../../global/stores/GlobalStore';
import LoginStore from './LoginStore';

interface IRegisterForm {
  rpo_search: string;
  isWithoutRpo: boolean;
  email: string;
  company_name: string;
  company_identifier: string;
  communication_method: boolean;
  personal_data: boolean;
  rpo_city: string;
  rpo_house_number: string;
  rpo_street: string;
  rpo_zip: string;
  rpo_legal_form: string;
}

function Register() {
  const { t } = useTranslation();
  const history = useHistory();

  const schema = Joi.object<IRegisterForm>({
    // rpo_search je povinné len keď NIE je ručné zadanie
    rpo_search: Joi.when('isWithoutRpo', {
      is: true,
      then: Joi.string().allow('').optional(),
      otherwise: Joi.string().required()
    }),
    isWithoutRpo: Joi.boolean().default(false),
    email: Joi.string()
      .custom((v, h) => (isEmailValid(v) ? v : h.error('any.invalid')))
      .required(),
    company_name: Joi.string().required(),
    company_identifier: Joi.string().length(8).required(),
    communication_method: Joi.boolean().default(false),
    personal_data: Joi.boolean().default(false),
    rpo_city: Joi.string().allow('').optional(),
    rpo_house_number: Joi.string().allow('').optional(),
    rpo_street: Joi.string().allow('').optional(),
    rpo_zip: Joi.string().allow('').optional(),
    rpo_legal_form: Joi.string().allow('').optional()
  });

  const form = useForm<IRegisterForm>({
    resolver: joiResolver(schema),
    defaultValues: {
      rpo_search: '',
      isWithoutRpo: false,
      email: '',
      company_name: '',
      company_identifier: '',
      communication_method: false,
      personal_data: false,
      rpo_city: '',
      rpo_house_number: '',
      rpo_street: '',
      rpo_zip: '',
      rpo_legal_form: ''
    }
  });
  const { control, handleSubmit, watch, setValue } = form;

  const personalData = watch('personal_data');
  const isWithoutRpo = watch('isWithoutRpo');

  const clearCompanyFields = () => {
    setValue('rpo_search', '');
    setValue('company_name', '');
    setValue('company_identifier', '');
    setValue('rpo_city', '');
    setValue('rpo_house_number', '');
    setValue('rpo_street', '');
    setValue('rpo_zip', '');
    setValue('rpo_legal_form', '');
  };

  const registerCall = useServerCall<any, any>({
    key: 'register',
    mode: 'control',
    request: { method: 'POST' },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: () => {
      GlobalStore.loading(false);
      GlobalStore.openConfirmRegistrationDialog();
      history.push('/login');
    },
    onError: () => {
      GlobalStore.loading(false);
    }
  });

  const onSubmit = handleSubmit(async (values) => {
    GlobalStore.loading(true);

    const data: any = {
      email: values.email,
      company_name: values.company_name,
      company_identifier: values.company_identifier,
      personal_data: values.personal_data,
      communication_method: values.communication_method
    };

    // Pri vyhľadaní cez RPO posielame aj adresné údaje (ekvivalent zp loginStore.registration)
    if (!values.isWithoutRpo) {
      data.city_code = values.rpo_city;
      data.house_number = values.rpo_house_number;
      data.street = values.rpo_street;
      data.zip = values.rpo_zip;
      data.legal_form_name_ext = values.rpo_legal_form;
    }

    const token = await LoginStore.getRecaptchaToken('client_register');
    registerCall.run({ url: API.register(token), data });
  });

  return (
    <>
      <BaseHeader />
      <Box mt={12}>
        <Grid container justifyContent='center'>
          <Grid item xs={12} md={7}>
            <Grid container direction='column' spacing={3}>
              <Grid item className='textCenter'>
                <Typography variant='h3'>{t('register.title')}</Typography>
              </Grid>
              <Grid item>
                <Paper variant='elevation' style={{ backgroundColor: 'transparent' }}>
                  <Box p={2}>
                    <form onSubmit={onSubmit}>
                      <FormProvider {...form}>
                        <Grid item container direction='column' spacing={1}>
                          <Grid item container spacing={1}>
                            {/* RPO vyhľadávanie podľa IČO/názvu */}
                            <Grid item xs={12} md={6}>
                              <Controller
                                name='rpo_search'
                                control={control}
                                render={({ field }) => (
                                  <XsAutocompleteServerWithoutLoad
                                    {...field}
                                    label={t('register.rpo_search_label')}
                                    required
                                    minLengthForSearch={3}
                                    disabled={isWithoutRpo}
                                    request={{
                                      uri: RPO_SEARCH_URL,
                                      includeToken: false,
                                      payload: {
                                        search: (v: string) => getFilters([`search=${v}`], { row_count_full: 200 })
                                      }
                                    }}
                                    identFn={(o: any) => o.identifier}
                                    labelFn={(o: any) => (o ? `${o.name} ${o.identifier}` : '')}
                                    onApply={(o: any) => {
                                      if (!o) return;
                                      setValue('company_name', o.name ?? '');
                                      setValue('company_identifier', o.identifier ?? '');
                                      setValue('rpo_city', o.city_id ?? '');
                                      setValue('rpo_house_number', o.house_number ?? '');
                                      setValue('rpo_street', o.street_plain ?? '');
                                      setValue('rpo_zip', o.zip_plain ?? '');
                                      setValue('rpo_legal_form', o.legal_form_name_ext ?? '');
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            {/* Prepínač na ručné zadanie IČO a názvu */}
                            <Grid item xs={12} md={6}>
                              <Controller
                                name='isWithoutRpo'
                                control={control}
                                render={({ field }) => (
                                  <XsCheckbox
                                    controllerProps={field}
                                    label={t('register.without_rpo_label')}
                                    onChange={() => clearCompanyFields()}
                                  />
                                )}
                              />
                            </Grid>
                            {/* Ručné polia (len pri zaškrtnutom "zadám ručne") */}
                            {isWithoutRpo && (
                              <>
                                <Grid item xs={12} md={7}>
                                  <Controller
                                    name='company_name'
                                    control={control}
                                    render={({ field }) => (
                                      <XsTextField {...field} label={t('register.company_name_label')} />
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <Controller
                                    name='company_identifier'
                                    control={control}
                                    render={({ field }) => (
                                      <XsNumberField {...field} label={t('register.company_identifier_label')} />
                                    )}
                                  />
                                  <Typography variant='caption' className='greyDark'>
                                    {t('register.identifier_tooltip')}
                                  </Typography>
                                </Grid>
                              </>
                            )}
                          </Grid>
                          <Grid item container spacing={4}>
                            <Grid item xs={12} className='mt-4'>
                              <Typography variant='body2'>{t('register.company_name_hint')}</Typography>
                            </Grid>
                            <Grid item xs={12} md={7}>
                              <Controller
                                name='email'
                                control={control}
                                render={({ field }) => <XsTextField {...field} label={t('register.email_label')} />}
                              />
                            </Grid>
                          </Grid>
                          <Grid item container spacing={2} className='mt-4'>
                            <Grid item xs={12}>
                              <Typography variant='body1' className='bold'>
                                {t('register.communication_title')}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Controller
                                name='communication_method'
                                control={control}
                                render={({ field }) => (
                                  <XsCheckbox
                                    controllerProps={field}
                                    label={t('register.communication_method_label')}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          <Grid item container spacing={1} className='mt-4'>
                            <Grid item>
                              <Typography variant='body1'>{t('register.personal_data_title')}</Typography>
                            </Grid>
                            <Grid item>
                              <Link
                                variant='body1'
                                className='pointer'
                                underline='always'
                                onClick={() => window.open(t('register.personal_data_link'), '_blank')}
                              >
                                {t('register.personal_data_link')}
                              </Link>
                            </Grid>
                          </Grid>
                          <Grid item>
                            <Controller
                              name='personal_data'
                              control={control}
                              render={({ field }) => (
                                <XsCheckbox controllerProps={field} label={t('register.personal_data_label')} />
                              )}
                            />
                          </Grid>
                          {GlobalStore.enabledRecaptchaV2 && (
                            <Grid item>
                              <ReCAPTCHA
                                sitekey={settings.SITE_KEY_V2 ?? ''}
                                onChange={(tok) => (GlobalStore.tokenRecaptchaV2 = tok ?? undefined)}
                              />
                            </Grid>
                          )}
                          <Grid item container justifyContent='flex-end'>
                            <Grid item>
                              <XsButton
                                type='submit'
                                variant='contained'
                                label='register.submit'
                                disabled={!personalData}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </FormProvider>
                    </form>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default observer(Register);
